import { useCallback, useEffect, useState } from 'react';
import { errorMessageOf, isAbortError, isAuthError } from '@/lib/errors';
import { useSessionStore } from '@/store/useSessionStore';

interface AsyncDataResult<TData> {
  data: TData | null;
  errorMessage: string | null;
  isLoading: boolean;
  reload: () => void;
}

/**
 * Run an abortable async loader on mount (and when `dependencies` change),
 * exposing loading/error/data plus a manual `reload`. Auth errors flip the
 * session store to "needs reconnect" so the shell can prompt for a new token.
 */
export function useAsyncData<TData>(
  loader: (signal: AbortSignal) => Promise<TData>,
  dependencies: ReadonlyArray<unknown> = [],
): AsyncDataResult<TData> {
  const [data, setData] = useState<TData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const flagAuthExpired = useSessionStore((state) => state.flagAuthExpired);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setErrorMessage(null);

    loader(controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setData(result);
        }
      })
      .catch((error) => {
        if (controller.signal.aborted || isAbortError(error)) {
          return;
        }
        if (isAuthError(error)) {
          flagAuthExpired();
        }
        setErrorMessage(errorMessageOf(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, reloadToken]);

  return { data, errorMessage, isLoading, reload };
}
